/**
 * Alert, Card, Dialog, Toast and the auth shell.
 *
 * Alerts and toasts differ by permanence and by who is listening: an alert is
 * part of the page and is described by `role="alert"` only when it reports a
 * failure, while a toast is transient and lives in a polite live region so it
 * never interrupts what a screen reader is already saying.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { Button } from './Button';
import { Icon, type IconName } from './Icon';
import { BrandMark } from './BrandMark';

export type Tone = 'info' | 'success' | 'warning' | 'danger';

const TONE_ICON: Record<Tone, IconName> = {
  info: 'info',
  success: 'check',
  warning: 'alert',
  danger: 'alert',
};

export interface AlertProps {
  tone?: Tone;
  title?: string;
  children: ReactNode;
  action?: ReactNode;
}

export function Alert({ tone = 'info', title, children, action }: AlertProps) {
  return (
    <div
      className={`mm-alert mm-alert--${tone}`}
      // Only failures interrupt. A success notice is read in turn.
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      <span className="mm-alert__icon">
        <Icon name={TONE_ICON[tone]} size={19} />
      </span>
      <div>
        {title ? <p className="mm-alert__title">{title}</p> : null}
        <p className="mm-alert__body">{children}</p>
        {action ? <div style={{ marginTop: 'var(--space-3)' }}>{action}</div> : null}
      </div>
    </div>
  );
}

export interface CardProps {
  title?: string;
  children: ReactNode;
  variant?: 'default' | 'feature' | 'paper';
}

export function Card({ title, children, variant = 'default' }: CardProps) {
  const classes = ['mm-card', variant === 'feature' ? 'mm-card--feature' : '',
    variant === 'paper' ? 'mm-card--paper' : ''].filter(Boolean).join(' ');
  return (
    <section className={classes}>
      {title ? <h3 className="mm-card__title">{title}</h3> : null}
      {children}
    </section>
  );
}

export interface DialogProps {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function Dialog({
  open,
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus moves into the dialog on open, and Escape always closes it. Without
  // both, a keyboard user is stranded behind a scrim they cannot see past.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.querySelector<HTMLElement>('button, [href], input')?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCancel();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;
      // A modal that lets focus escape into the page behind it is not modal.
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'button:not(:disabled), [href], input:not(:disabled), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus();
    };
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="mm-dialog-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div
        ref={panelRef}
        className="mm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mm-dialog-title"
      >
        <h2 className="mm-dialog__title" id="mm-dialog-title">
          {title}
        </h2>
        <div className="mm-dialog__body">{children}</div>
        <div className="mm-dialog__actions">
          <Button variant="tertiary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant={destructive ? 'destructive' : 'primary'} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export interface ToastMessage {
  id: string;
  tone?: Tone;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}

export function ToastRegion({ toasts }: { toasts: ToastMessage[] }) {
  return (
    <div className="mm-toast-region" role="status" aria-live="polite" aria-atomic="false">
      {toasts.map((toast) => (
        <div key={toast.id} className={`mm-toast mm-toast--${toast.tone ?? 'info'}`}>
          <Icon name={TONE_ICON[toast.tone ?? 'info']} size={18} />
          <span>{toast.message}</span>
          {toast.actionLabel && toast.onAction ? (
            <span className="mm-toast__action">
              <Button variant="tertiary" onClick={toast.onAction}>
                {toast.actionLabel}
              </Button>
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * The shell every auth screen sits in. Narrow, centred, safe-area aware.
 *
 * The layout moves focus to its own heading whenever the heading changes, which
 * is the behaviour that makes client-side navigation survivable for a screen
 * reader. Doing it here rather than in each screen means it cannot be forgotten
 * on the eighth screen somebody adds — and means "which heading" and "what gets
 * focused" can never disagree.
 */
export function AuthLayout({
  heading,
  sub,
  children,
  footer,
}: {
  heading: string;
  sub?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // Announce the new screen without asking Safari to reposition the whole
    // authentication scroller around a programmatic heading focus.
    const frame = requestAnimationFrame(() => headingRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, [heading]);

  return (
    <div className="mm-auth">
      <div className="mm-auth__inner">
        <div className="mm-auth__brand">
          <span style={{ color: 'var(--accent)', display: 'inline-flex' }}>
            <BrandMark size={34} />
          </span>
          <span className="mm-auth__wordmark">MusicMentor</span>
        </div>
        <div className="mm-card mm-card--feature">
          {/* tabIndex -1: focusable programmatically, but not a tab stop. */}
          <h1 className="mm-auth__heading" ref={headingRef} tabIndex={-1}>
            {heading}
          </h1>
          {sub ? <p className="mm-auth__sub">{sub}</p> : null}
          <div style={{ marginTop: 'var(--space-6)' }}>{children}</div>
        </div>
        {footer ? <p className="mm-auth__footer">{footer}</p> : null}
      </div>
    </div>
  );
}
